import React, { useState, useEffect, useCallback } from 'react';
import { MapPin, CheckCircle2, AlertTriangle, Flag, Undo2, Download, Loader2, ChevronDown, ChevronUp, Users } from 'lucide-react';
import { Button } from './ui/button';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import AuthImage from './AuthImage';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const authHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const fmtIST = (iso) => (iso ? new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata' }) : '—');
const fmtHours = (h) => {
  if (h === null || h === undefined) return '—';
  const t = Math.round(h * 60);
  return `${Math.floor(t / 60)}h ${t % 60}m`;
};

// IST date helpers for presets.
// IMPORTANT: apply the Asia/Kolkata timezone exactly ONCE. We derive the
// current IST calendar date as a YYYY-MM-DD string, then do all arithmetic in
// UTC (parseISO builds a midnight-UTC Date) so there is no double tz shift.
const istNow = () => new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
const parseISO = (s) => { const [y, m, d] = s.split('-').map(Number); return new Date(Date.UTC(y, m - 1, d)); };
const fmt = (dt) => dt.toISOString().slice(0, 10);

const presets = {
  today: () => { const s = istNow(); return [s, s]; },
  week: () => { const t = parseISO(istNow()); const s = new Date(t); s.setUTCDate(t.getUTCDate() - t.getUTCDay()); return [fmt(s), istNow()]; },
  month: () => { const t = parseISO(istNow()); const s = new Date(Date.UTC(t.getUTCFullYear(), t.getUTCMonth(), 1)); return [fmt(s), istNow()]; },
  lastMonth: () => { const t = parseISO(istNow()); const s = new Date(Date.UTC(t.getUTCFullYear(), t.getUTCMonth() - 1, 1)); const e = new Date(Date.UTC(t.getUTCFullYear(), t.getUTCMonth(), 0)); return [fmt(s), fmt(e)]; },
};

const STATUS_OPTS = ['all', 'pending', 'approved', 'flagged', 'auto_closed'];

const StatusBadge = ({ status }) => {
  const map = {
    pending: 'bg-amber-100 text-amber-800',
    approved: 'bg-green-100 text-green-700',
    flagged: 'bg-red-100 text-red-700',
    auto_closed: 'bg-gray-200 text-gray-600',
  };
  return <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${map[status] || map.pending}`}>{status}</span>;
};

const PhotoBlock = ({ label, block, status, expanded, onEnlarge }) => {
  if (!block) {
    return (
      <div className="flex-1 border rounded-lg p-2 bg-gray-50">
        <p className="text-xs font-semibold text-gray-600 mb-1">{label}</p>
        <p className="text-xs text-gray-400">{status === 'auto_closed' ? 'Auto-closed (no check-out)' : 'Not checked out'}</p>
      </div>
    );
  }
  return (
    <div className="flex-1 border rounded-lg p-2">
      <p className="text-xs font-semibold text-gray-600 mb-1">{label} · {fmtIST(block.time)}</p>
      {expanded && (
        <AuthImage path={block.photo_url} alt={label} className="w-full h-32 object-cover rounded border cursor-pointer mb-1" onClick={() => onEnlarge(block.photo_url)} />
      )}
      <div className="flex items-center justify-between">
        <a href={block.maps_url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 flex items-center gap-1">
          <MapPin className="h-3 w-3" /> View on Maps
        </a>
        {block.accuracy_meters != null && <span className="text-[10px] text-gray-400">±{Math.round(block.accuracy_meters)}m</span>}
      </div>
    </div>
  );
};

const RecordCard = ({ rec, selected, onSelect, onApprove, onFlag, onUnflag, onEnlarge }) => {
  const [expanded, setExpanded] = useState(false); // lazy-load photos only when expanded
  return (
    <div className="bg-white border rounded-xl p-3 shadow-sm" data-testid={`att-record-${rec.id}`}>
      <div className="flex items-start gap-3">
        <input
          type="checkbox"
          data-testid={`att-checkbox-${rec.id}`}
          checked={selected}
          onChange={() => onSelect(rec.id)}
          className="mt-1 h-4 w-4"
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="min-w-0">
              <p className="font-semibold text-gray-900 truncate">{rec.staff_name} <span className="text-xs font-normal text-gray-500">· {rec.staff_phone}</span></p>
              <p className="text-xs text-gray-500">{rec.venue_name} · {rec.date} · {fmtHours(rec.hours_worked)}</p>
            </div>
            <StatusBadge status={rec.status} />
          </div>

          <button onClick={() => setExpanded(!expanded)} className="mt-2 text-xs text-blue-600 flex items-center gap-1" data-testid={`att-toggle-${rec.id}`}>
            {expanded ? <><ChevronUp className="h-3 w-3" /> Hide photos</> : <><ChevronDown className="h-3 w-3" /> View photos</>}
          </button>

          <div className="flex gap-2 mt-2">
            <PhotoBlock label="Check In" block={rec.check_in} status={rec.status} expanded={expanded} onEnlarge={onEnlarge} />
            <PhotoBlock label="Check Out" block={rec.check_out} status={rec.status} expanded={expanded} onEnlarge={onEnlarge} />
          </div>

          {rec.status === 'flagged' && rec.admin_note && (
            <div className="mt-2 text-xs bg-amber-50 text-amber-800 rounded p-2">Note: {rec.admin_note}</div>
          )}

          <div className="flex gap-2 mt-3">
            {(rec.status === 'pending' || rec.status === 'flagged') && (
              <Button size="sm" className="bg-green-600 hover:bg-green-700 h-8" data-testid={`att-approve-${rec.id}`} onClick={() => onApprove(rec.id)}>
                <CheckCircle2 className="h-4 w-4 mr-1" /> Approve
              </Button>
            )}
            {(rec.status === 'pending' || rec.status === 'approved') && (
              <Button size="sm" variant="outline" className="text-amber-700 border-amber-300 h-8" data-testid={`att-flag-${rec.id}`} onClick={() => onFlag(rec)}>
                <Flag className="h-4 w-4 mr-1" /> Flag
              </Button>
            )}
            {rec.status === 'flagged' && (
              <Button size="sm" variant="outline" className="h-8" data-testid={`att-unflag-${rec.id}`} onClick={() => onUnflag(rec.id)}>
                <Undo2 className="h-4 w-4 mr-1" /> Unflag
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const StaffAttendance = () => {
  const { user, isSuperAdmin } = useAuth();
  const [range, setRange] = useState(presets.month());
  const [venueId, setVenueId] = useState('');
  const [staffId, setStaffId] = useState('');
  const [status, setStatus] = useState('all');
  const [facilities, setFacilities] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [flagTarget, setFlagTarget] = useState(null);
  const [flagNote, setFlagNote] = useState('');
  const [enlarge, setEnlarge] = useState(null);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    if (isSuperAdmin) api.getFacilities().then((f) => setFacilities(f || [])).catch(() => {});
  }, [isSuperAdmin]);

  // staff list (scoped). superadmin filters by chosen venue.
  useEffect(() => {
    const load = async () => {
      try {
        const url = isSuperAdmin && venueId ? `${API_URL}/api/auth/users?facility_id=${venueId}` : `${API_URL}/api/auth/users`;
        const resp = await fetch(url, { headers: authHeaders() });
        const data = await resp.json();
        setStaffList((data || []).filter((u) => u.role === 'venue_manager'));
      } catch (e) { /* non-fatal */ }
    };
    load();
  }, [isSuperAdmin, venueId]);

  const load = useCallback(async () => {
    setLoading(true);
    setSelected(new Set());
    try {
      const params = new URLSearchParams({ date_from: range[0], date_to: range[1], page: 1, page_size: 100 });
      if (isSuperAdmin && venueId) params.set('venue_id', venueId);
      if (staffId) params.set('staff_id', staffId);
      if (status !== 'all') params.set('status', status);
      const resp = await fetch(`${API_URL}/api/attendance/records?${params}`, { headers: authHeaders() });
      const data = await resp.json();
      if (!resp.ok) { toast.error(data.detail || 'Failed to load records'); setRecords([]); return; }
      setRecords(data.records || []);
    } catch (e) {
      console.error('load records failed', e);
      toast.error('Failed to load records');
    } finally {
      setLoading(false);
    }
  }, [range, venueId, staffId, status, isSuperAdmin]);

  useEffect(() => { load(); }, []); // initial

  const summary = records.reduce((acc, r) => { acc.total++; acc[r.status] = (acc[r.status] || 0) + 1; return acc; }, { total: 0 });

  const toggleSelect = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const selectAllPending = () => setSelected(new Set(records.filter((r) => r.status === 'pending').map((r) => r.id)));

  const doApprove = async (id) => {
    const resp = await fetch(`${API_URL}/api/attendance/records/${id}/approve`, { method: 'POST', headers: authHeaders() });
    const data = await resp.json();
    if (!resp.ok) return toast.error(data.detail || 'Approve failed');
    setRecords((rs) => rs.map((r) => (r.id === id ? data : r)));
    toast.success('Approved');
  };
  const doUnflag = async (id) => {
    const resp = await fetch(`${API_URL}/api/attendance/records/${id}/unflag`, { method: 'POST', headers: authHeaders() });
    const data = await resp.json();
    if (!resp.ok) return toast.error(data.detail || 'Unflag failed');
    setRecords((rs) => rs.map((r) => (r.id === id ? data : r)));
    toast.success('Unflagged');
  };
  const submitFlag = async () => {
    if (flagNote.trim().length < 5) return toast.error('Note must be at least 5 characters');
    const resp = await fetch(`${API_URL}/api/attendance/records/${flagTarget.id}/flag`, {
      method: 'POST', headers: { ...authHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify({ note: flagNote.trim() }),
    });
    const data = await resp.json();
    if (!resp.ok) return toast.error(data.detail || 'Flag failed');
    setRecords((rs) => rs.map((r) => (r.id === flagTarget.id ? data : r)));
    setFlagTarget(null); setFlagNote('');
    toast.success('Flagged');
  };

  const bulkApprove = async () => {
    const ids = [...selected];
    if (!ids.length) return;
    if (!window.confirm(`Approve ${ids.length} selected record(s)?`)) return;
    const resp = await fetch(`${API_URL}/api/attendance/bulk-approve`, {
      method: 'POST', headers: { ...authHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify({ record_ids: ids }),
    });
    const data = await resp.json();
    if (!resp.ok) return toast.error(data.detail || 'Bulk approve failed');
    toast.success(`Approved ${data.approved_count}, skipped ${data.skipped_count}`);
    setSelected(new Set());
    load();
  };

  const doExport = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams({ date_from: range[0], date_to: range[1] });
      if (isSuperAdmin && venueId) params.set('venue_id', venueId);
      if (staffId) params.set('staff_id', staffId);
      if (status !== 'all') params.set('status', status);
      const resp = await fetch(`${API_URL}/api/attendance/export?${params}`, { headers: authHeaders() });
      if (!resp.ok) { toast.error('Export failed'); return; }
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `Attendance_${range[0]}_to_${range[1]}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } finally { setExporting(false); }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-4 pb-24 lg:pb-8" data-testid="staff-attendance-page">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2"><Users className="h-5 w-5" /> Staff Attendance</h1>
        <Button onClick={doExport} disabled={exporting} variant="outline" size="sm" data-testid="att-export-btn">
          {exporting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Download className="h-4 w-4 mr-1" />} Export to Excel
        </Button>
      </div>

      {/* Filters */}
      <div className="bg-white border rounded-xl p-3 mb-4 space-y-3">
        <div className="flex flex-wrap gap-2">
          {[['Today', 'today'], ['This Week', 'week'], ['This Month', 'month'], ['Last Month', 'lastMonth']].map(([label, key]) => (
            <Button key={key} size="sm" variant="outline" data-testid={`att-preset-${key}`} onClick={() => setRange(presets[key]())}>{label}</Button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <input type="date" value={range[0]} onChange={(e) => setRange([e.target.value, range[1]])} className="text-sm border rounded px-2 py-1.5" data-testid="att-date-from" />
          <span className="text-gray-400">→</span>
          <input type="date" value={range[1]} onChange={(e) => setRange([range[0], e.target.value])} className="text-sm border rounded px-2 py-1.5" data-testid="att-date-to" />
          {isSuperAdmin ? (
            <select value={venueId} onChange={(e) => { setVenueId(e.target.value); setStaffId(''); }} className="text-sm border rounded px-2 py-1.5" data-testid="att-venue-select">
              <option value="">All Venues</option>
              {facilities.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
            </select>
          ) : (
            <span className="text-sm text-gray-600 px-2 py-1.5 bg-gray-50 rounded">{user?.facility_name || 'My Venue'}</span>
          )}
          <select value={staffId} onChange={(e) => setStaffId(e.target.value)} className="text-sm border rounded px-2 py-1.5" data-testid="att-staff-select">
            <option value="">All Staff</option>
            {staffList.map((s) => <option key={s.id || s.email} value={s.id}>{s.name}</option>)}
          </select>
          <select value={status} onChange={(e) => setStatus(e.target.value)} className="text-sm border rounded px-2 py-1.5 capitalize" data-testid="att-status-select">
            {STATUS_OPTS.map((s) => <option key={s} value={s}>{s === 'all' ? 'All Statuses' : s}</option>)}
          </select>
          <Button size="sm" onClick={load} data-testid="att-apply-btn" className="bg-blue-600 hover:bg-blue-700">Apply</Button>
        </div>
      </div>

      {/* Summary strip */}
      <div className="grid grid-cols-5 gap-2 mb-4" data-testid="att-summary-strip">
        {[['Total', summary.total, 'bg-gray-100 text-gray-700'], ['Pending', summary.pending || 0, 'bg-amber-100 text-amber-800'], ['Approved', summary.approved || 0, 'bg-green-100 text-green-700'], ['Flagged', summary.flagged || 0, 'bg-red-100 text-red-700'], ['Auto-closed', summary.auto_closed || 0, 'bg-gray-200 text-gray-600']].map(([label, val, cls]) => (
          <div key={label} className={`rounded-lg p-2 text-center ${cls}`}>
            <p className="text-lg font-bold">{val}</p>
            <p className="text-[11px]">{label}</p>
          </div>
        ))}
      </div>

      {/* Bulk action bar */}
      <div className="flex items-center gap-2 mb-3">
        <Button size="sm" variant="outline" onClick={selectAllPending} data-testid="att-select-all-pending">Select All Pending</Button>
        {selected.size > 0 && (
          <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={bulkApprove} data-testid="att-bulk-approve-btn">
            Approve {selected.size} selected
          </Button>
        )}
      </div>

      {/* Records */}
      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>
      ) : records.length === 0 ? (
        <p className="text-center text-gray-400 py-10">No attendance records for this filter.</p>
      ) : (
        <div className="space-y-3">
          {records.map((r) => (
            <RecordCard
              key={r.id}
              rec={r}
              selected={selected.has(r.id)}
              onSelect={toggleSelect}
              onApprove={doApprove}
              onFlag={(rec) => { setFlagTarget(rec); setFlagNote(''); }}
              onUnflag={doUnflag}
              onEnlarge={setEnlarge}
            />
          ))}
        </div>
      )}

      {/* Flag modal */}
      {flagTarget && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="att-flag-modal">
          <div className="bg-white rounded-xl p-4 w-full max-w-md space-y-3">
            <h3 className="font-semibold flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-amber-600" /> Flag attendance</h3>
            <p className="text-sm text-gray-500">{flagTarget.staff_name} · {flagTarget.date}</p>
            <textarea
              data-testid="att-flag-note"
              value={flagNote}
              onChange={(e) => setFlagNote(e.target.value)}
              placeholder="Reason (min 5 characters)…"
              rows={3}
              className="w-full border rounded p-2 text-sm"
            />
            {flagNote.trim().length > 0 && flagNote.trim().length < 5 && (
              <p data-testid="att-flag-error" className="text-xs text-red-600">Note must be at least 5 characters.</p>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setFlagTarget(null)} data-testid="att-flag-cancel">Cancel</Button>
              <Button size="sm" className="bg-amber-600 hover:bg-amber-700" onClick={submitFlag} data-testid="att-flag-submit">Flag</Button>
            </div>
          </div>
        </div>
      )}

      {/* Enlarge modal */}
      {enlarge && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setEnlarge(null)} data-testid="att-photo-modal">
          <AuthImage path={enlarge} alt="enlarged" className="max-w-full max-h-[85vh] object-contain rounded-lg" />
        </div>
      )}
    </div>
  );
};

export default StaffAttendance;
